import type { AuthorityLimit, AuthorityLimits, Band, Policy, Rung } from './types.js'

/** The prices a limit can be measured from, plus what the drivers add above cost. */
export interface LimitPrices {
  ask: number
  target: number
  floor: number
  cost: number
  driverSum: number
}

export interface BandInputs {
  price: number
  ask: number
  target: number
  floor: number
  /** needed only for limits measured from cost or as a share of the drivers */
  cost?: number | undefined
  driverSum?: number | undefined
  policy: Pick<Policy, 'authority'>
}

/** What the drivers add above cost: the sum of the driver rungs. Older snapshots lack the field. */
export function driverSumOf(ladder: Rung[]): number {
  return ladder.filter((r) => r.kind === 'driver').reduce((s, r) => s + r.amount, 0)
}

/** The limits in force: explicit ones, or the ones implied by repFloor and hardFloor. */
export function authorityOf(policy: Pick<Policy, 'authority'>): AuthorityLimits {
  const a = policy.authority
  if (a.limits) return a.limits
  return {
    rep: { ref: a.repFloor === 'ask' ? 'ask' : 'target', unit: 'pct', below: 0 },
    supervisor: { ref: 'floor', unit: 'pct', below: 0 },
    head: a.hardFloor === 'blocked' ? { ref: 'floor', unit: 'pct', below: 0 } : null,
  }
}

/** The price a limit resolves to for one article–customer pair. */
export function limitPrice(l: AuthorityLimit, p: LimitPrices): number {
  const ref = p[l.ref]
  if (l.unit === 'pct') return ref * (1 - l.below)
  if (l.unit === 'eur') return ref - l.below
  return ref - l.below * p.driverSum
}

export interface ResolvedLimits {
  rep: number
  supervisor: number
  head: number | null
}

export function resolveLimits(policy: Pick<Policy, 'authority'>, p: LimitPrices): ResolvedLimits {
  const a = authorityOf(policy)
  return {
    rep: limitPrice(a.rep, p),
    supervisor: limitPrice(a.supervisor, p),
    head: a.head ? limitPrice(a.head, p) : null,
  }
}

function pricesOf(i: BandInputs): LimitPrices {
  return { ask: i.ask, target: i.target, floor: i.floor, cost: i.cost ?? 0, driverSum: i.driverSum ?? 0 }
}

/**
 * §6.1 — who may accept a price.
 *
 *   ≥ rep limit                          within       the rep decides
 *   supervisor limit ≤ price < rep       escalate     the supervisor decides
 *   < supervisor limit                   below_floor  the head of sales decides
 *   < head limit (when one is set)       blocked      nobody; see isBlocked()
 *
 * Without explicit limits the rep limit is the target (or the ask) and the supervisor limit is
 * the floor, which is the spec's rule. The lowest line is evaluated first, so a supervisor limit
 * above the rep limit (the inverted case, floor above target) leaves the escalate band empty
 * rather than letting the rep accept a price below the floor.
 */
export function bandFor(i: BandInputs): Band {
  const l = resolveLimits(i.policy, pricesOf(i))
  if (i.price < l.supervisor) return 'below_floor'
  if (i.price < l.rep) return 'escalate'
  return 'within'
}

/** Below the head of sales' limit, when one is set: no role may accept the price. */
export function isBlocked(i: BandInputs): boolean {
  const l = resolveLimits(i.policy, pricesOf(i))
  return l.head !== null && i.price < l.head
}

export function bandLabel(band: Band): string {
  switch (band) {
    case 'within':
      return 'Within your authority'
    case 'escalate':
      return 'Needs sales director approval'
    case 'below_floor':
      return 'Needs general manager'
  }
}
